import React, { useState, useEffect } from 'react'
import PropTypes, { any } from 'prop-types'
import moment from 'moment'
import { DataGrid } from '@mui/x-data-grid'
import { useSelector } from 'react-redux'
import NoResultsFoundTableOverlay from '../../../../components/NoResultsFoundTableOverlay/no-results-found-table-overlay'
import Constants from '../../../../constants/en.json'
import StatusChip from '../../../../components/genericcomponents/StatusChip/status-chip'
import { IconButton } from '../../../../components/mui/Core/index'
import { EditIcon } from '../../../../components/mui/Icons'
import { hasUserPermission } from '../../../../utils/user-utils'
import { userPermissionConstants } from '../../../../constants/user-permission-constants'

function UserManagementTable({
  filterBy,
  filterValue,
  handleRowSelection,
  rowSelection,
  searchByOptions,
  handleRoleUpdateModalOpen,
}) {
  const users = useSelector((state) => state.userManagement.users)
  const isFetchingAllUsers = useSelector((state) => state.userManagement.isFetchingAllUsers)
  const authenticatedUser = useSelector((state) => state.login.authenticatedUser)
  const selectedSearchOption = searchByOptions.filter((option) => {
    return option.id === filterBy
  })
  const filterByDescription = selectedSearchOption[0]?.text || ''
  const filterByType = selectedSearchOption[0]?.type
  const noResultsValue = filterByType === 'date' ? moment.utc(filterValue).format('L') : filterValue

  const hasUserChangeRoleAccess = hasUserPermission({
    permissionCode: userPermissionConstants.CHANGE_ROLE,
    permissions: authenticatedUser?.permissions || [],
  })

  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    Actions: hasUserChangeRoleAccess,
  })

  const rows = users.map((user) => ({
    id: user?.userName || user?.id,
    firstName: user?.firstName,
    lastName: user?.lastName,
    clientName: user?.clientName,
    email: user?.email,
    role: user?.role,
    appName: user?.appName,
    dateAdded: user?.dateAdded,
    addedBy: user?.addedBy,
    status: user?.status,
  }))

  const handleEditUserRole = (event, rowData) => {
    event.stopPropagation()
    handleRoleUpdateModalOpen(rowData.row)
  }

  useEffect(() => {
    setColumnVisibilityModel({ Actions: hasUserChangeRoleAccess })
  }, [hasUserChangeRoleAccess])

  const columns = [
    {
      field: 'firstName',
      headerName: Constants.userManagement.FIRST_NAME,
      type: 'string',
      minWidth: 120,
      flex: 1,
      headerClassName: 'bold-header',
      valueGetter: (value, row) => (row.firstName === 'None' ? '-' : row.firstName),
    },
    {
      field: 'lastName',
      headerName: Constants.userManagement.LAST_NAME,
      minWidth: 130,
      type: 'string',
      flex: 1,
      valueGetter: (value, row) => (row.lastName === 'None' ? '-' : row.lastName),
    },
    {
      field: 'clientName',
      headerName: Constants.userManagement.ORGANIZATION,
      type: 'string',
      minWidth: 150,
      flex: 1,
    },
    {
      field: 'email',
      headerName: Constants.userManagement.EMAIL,
      type: 'string',
      minWidth: 190,
      flex: 1,
    },
    {
      field: 'role',
      headerName: Constants.userManagement.ROLE,
      minWidth: 120,
      flex: 1,
    },
    {
      field: 'dateAdded',
      headerName: Constants.userManagement.DATE_ADDED,
      headerAlign: 'right',
      align: 'right',
      minWidth: 180,
      flex: 1,
      valueGetter: (value) => value,
      valueFormatter: (value) => value && moment.utc(value).format('L'),
    },
    {
      field: 'addedBy',
      headerName: Constants.userManagement.ADDED_BY,
      type: 'string',
      minWidth: 150,
      flex: 1,
    },
    {
      field: 'status',
      headerName: Constants.userManagement.STATUS,
      type: 'string',
      minWidth: 150,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      flex: 0.9,
      renderCell: (cellValues) => {
        const status = cellValues.row?.status
        return <StatusChip status={status} />
      },
    },
    {
      field: 'Actions',
      headerName: Constants.userManagement.ACTIONS,
      type: 'string',
      minWidth: 120,
      flex: 0.5,
      sortable: false,
      renderCell: (row) => {
        return (
          <IconButton onClick={(event) => handleEditUserRole(event, row)}>
            <EditIcon color='action' />
          </IconButton>
        )
      },
    },
  ]

  const filterModel = {
    items: [{ field: filterBy, operator: 'contains', value: filterValue }],
  }

  const userManagementTableStyle = {
    minHeight: '160px',
    height: isFetchingAllUsers ? '400px' : 'auto',
    '& .MuiDataGrid-columnHeaders': {
      '& .MuiDataGrid-columnHeader:nth-of-type(2)': {
        paddingLeft: '0px',
      },
    },
    '& .MuiDataGrid-row': {
      '& .MuiDataGrid-cell:nth-of-type(2)': {
        paddingLeft: '0px',
      },
    },
  }

  return (
    <DataGrid
      sx={userManagementTableStyle}
      checkboxSelection
      hideFooterSelectedRowCount
      rows={rows}
      onRowSelectionModelChange={handleRowSelection}
      rowSelectionModel={rowSelection}
      columns={columns}
      loading={isFetchingAllUsers}
      filterModel={filterModel}
      initialState={{
        pagination: {
          paginationModel: { pageSize: 25, page: 0 },
        },
      }}
      columnVisibilityModel={columnVisibilityModel}
      pageSizeOptions={[25, 50]}
      spacing={1}
      slotProps={{
        pagination: {
          component: 'div',
          showLastButton: true,
          showFirstButton: true,
        },
        noResultsOverlay: {
          filterByDescription,
          noResultsValue,
        },
        loadingOverlay: {
          variant: 'skeleton',
          noRowsVariant: 'skeleton',
        },
      }}
      slots={{
        noResultsOverlay: NoResultsFoundTableOverlay,
      }}
      disableColumnFilter
    />
  )
}

UserManagementTable.propTypes = {
  filterBy: PropTypes.string.isRequired,
  filterValue: PropTypes.string.isRequired,
  handleRowSelection: PropTypes.func.isRequired,
  rowSelection: PropTypes.arrayOf(PropTypes.string).isRequired,
  searchByOptions: PropTypes.arrayOf(PropTypes.objectOf(any)).isRequired,
  handleRoleUpdateModalOpen: PropTypes.func.isRequired,
}

export default UserManagementTable
